// Copyright © SRGTech (Software Resonance Group Inc.). All rights reserved.
// SPDX-License-Identifier: LicenseRef-SRGTech-Proprietary
// See LICENSE and docs/legal/PROPRIETARY_COPYRIGHT.md.

/**
 * HTTP implementations of the administration client ports.
 *
 * Every method is a thin, declarative binding of one BFF route to one
 * validator. Business decisions live behind the BFF and the services it fronts
 * (`CST-016`, `ADM-001`): nothing here interprets policy, computes eligibility,
 * or decides authorization.
 *
 * Route shape follows ADR-0002 major URL versioning; the version prefix is part
 * of the configured base path so it is stated once.
 */

import {
  BOOTSTRAP_LOGIN_OPERATION,
  BOOTSTRAP_LOGIN_PATH,
  createHttpClient,
  type HttpClient,
  type HttpClientConfig,
  type QueryParams,
} from './http';
import type {
  ApiClients,
  ApprovalDecisionInput,
  ControlPlaneClient,
  ListQuery,
  SessionClient,
  TenantClient,
} from './ports';
import {
  registeredTenantSchema,
  knowledgeDocumentSchema,
  listResponseSchema,
  memoryRecordSchema,
  metricsSchema,
  roleSchema,
} from './schemas';
import {
  accessGrantSchema,
  aiServiceSchema,
  approvalDecisionResultSchema,
  approvalRequestSchema,
  auditEventViewSchema,
  connectorWithHealthSchema,
  controlPlaneSummarySchema,
  dashboardSummarySchema,
  dataSourceSchema,
  healthCheckSchema,
  knowledgeCollectionSchema,
  policyViewSchema,
  schemaMappingSchema,
  serviceHealthSchema,
  sessionViewSchema,
} from './views';

import {
  activationRotationResultSchema,
  amendedCollegeSchema,
  collegeOnboardingViewSchema,
  edgeProvisioningViewSchema,
  tenantCreateResultSchema,
  tenantOverviewPageSchema,
} from './lifecycle.v2';
import { z } from 'zod';

import {
  modelListSchema,
  modelViewSchema,
  providerListSchema,
  providerViewSchema,
  routingViewSchema,
} from './ai-settings';
import type { AiSettingsClient } from './ai-settings';
import {
  collegeCostPageSchema,
  dailyCostSeriesSchema,
  modelCostPageSchema,
  usageQuery,
  usageSummarySchema,
  type AiUsageClient,
} from './ai-usage';

/** Maximum page size the UI will request, mirroring the BFF bound (`API-001`). */
export const MAX_PAGE_SIZE = 100;

function toQuery(query: ListQuery | undefined): QueryParams {
  if (query === undefined) {
    return {};
  }
  const params: Record<string, string | number | undefined> = {};
  if (query.page !== undefined) {
    params['page'] = query.page;
  }
  if (query.pageSize !== undefined) {
    params['page_size'] = Math.min(query.pageSize, MAX_PAGE_SIZE);
  }
  if (query.search !== undefined && query.search !== '') {
    params['search'] = query.search;
  }
  return params;
}

function listGetter<TSchema extends z.ZodType>(http: HttpClient, path: string, item: TSchema) {
  const schema = listResponseSchema(item);
  return async (query?: ListQuery): Promise<z.infer<typeof schema>> =>
    http.request({ path, query: toQuery(query), schema, signal: query?.signal });
}

/**
 * A page getter for a v2 collection whose envelope is part of its contract.
 *
 * Separate from {@link listGetter}: that one wraps an item schema in the v1
 * `ListResponse` envelope, and the v2 college list declares its own page shape.
 * Reusing the v1 wrapper would have validated the rows against v2 and the
 * envelope against v1, which is the sort of half-checked boundary that reads as
 * validated.
 */
function listGetterV2<TPage>(http: HttpClient, path: string, page: z.ZodType<TPage>) {
  return async (query?: ListQuery): Promise<TPage> =>
    http.request({ path, query: toQuery(query), schema: page, signal: query?.signal });
}

function createSessionClient(http: HttpClient): SessionClient {
  return {
    async getSession(signal?: AbortSignal) {
      return http.request({ path: '/session', schema: sessionViewSchema, signal });
    },
    async signIn(input) {
      await http.request({
        path: BOOTSTRAP_LOGIN_PATH,
        method: 'POST',
        // Sign-in precedes the double-submit cookie the BFF mints on success,
        // so this one request states that it carries no CSRF token. The
        // transport honours that only for this operation on this path with
        // this method; the BFF protects the route with exact-`Origin`
        // validation and rate limiting instead of double-submit.
        csrf: 'not-required',
        operation: BOOTSTRAP_LOGIN_OPERATION,
        body: { username: input.username, password: input.password },
        // Nothing in the response is read. Validating it as unknown states
        // that: the session is established by the cookie, and a body the UI
        // consumed would be a credential the UI held.
        schema: z.unknown(),
        signal: input.signal,
      });
    },
    async signOut(signal?: AbortSignal) {
      await http.request({
        path: '/session/logout',
        method: 'POST',
        schema: z.unknown(),
        signal,
      });
    },
  };
}

function createControlPlaneClient(http: HttpClient): ControlPlaneClient {
  const base = '/control-plane';

  return {
    async getSummary(signal?: AbortSignal) {
      // Validated against the counters Platform actually publishes, not the
      // richer observability shape the tenant summary uses.
      return http.request({ path: `${base}/summary`, schema: controlPlaneSummarySchema, signal });
    },
    listTenants: listGetter(http, `${base}/tenants`, registeredTenantSchema),
    async registerCollege(input) {
      // The three contract fields and nothing else. `address` is sent only
      // when supplied, so an omitted optional stays omitted rather than
      // becoming an explicit empty string the server did not receive from the
      // operator.
      const body: Record<string, string> = { name: input.name, code: input.code };
      if (input.address !== undefined) {
        body['address'] = input.address;
      }
      return http.request({
        path: `${base}/tenants`,
        method: 'POST',
        body,
        schema: tenantCreateResultSchema,
        signal: input.signal,
      });
    },
    // --- the college lifecycle (ADR-0076) -------------------------------
    //
    // Six fixed calls. Each names its own BFF route; none takes a path, a verb,
    // or a scope from a caller, so a component cannot turn one operation into
    // another. Every one is validated against the v2 contract before it reaches
    // a screen.
    listCollegeOverview: listGetterV2(http, `${base}/tenants`, tenantOverviewPageSchema),
    async amendCollege(input) {
      // Only the fields the caller actually named. An absent field means
      // "unchanged" upstream, which is not the same as clearing it.
      const body: Record<string, string> = {};
      if (input.name !== undefined) {
        body['name'] = input.name;
      }
      if (input.address !== undefined) {
        body['address'] = input.address;
      }
      return http.request({
        path: `${base}/tenants/${encodeURIComponent(input.tenantId)}`,
        method: 'PUT',
        body,
        schema: amendedCollegeSchema,
        signal: input.signal,
      });
    },
    async deleteCollege(input) {
      await http.request({
        path: `${base}/tenants/${encodeURIComponent(input.tenantId)}`,
        method: 'DELETE',
        // A `204` carries nothing, and demanding a body of it would turn a
        // successful deletion into a validation failure.
        schema: z.unknown(),
        signal: input.signal,
      });
    },
    async deactivateCollege(input) {
      return http.request({
        path: `${base}/tenants/${encodeURIComponent(input.tenantId)}/deactivate`,
        method: 'POST',
        schema: amendedCollegeSchema,
        signal: input.signal,
      });
    },
    async rotateActivationCode(input) {
      return http.request({
        path: `${base}/tenants/${encodeURIComponent(input.tenantId)}/activation-code`,
        method: 'POST',
        schema: activationRotationResultSchema,
        signal: input.signal,
      });
    },
    async requestEdgeProvisioning(input) {
      return http.request({
        path: `${base}/tenants/${encodeURIComponent(input.tenantId)}/edge-provisioning`,
        method: 'POST',
        schema: edgeProvisioningViewSchema,
        signal: input.signal,
      });
    },
    async readEdgeProvisioning(input) {
      return http.request({
        path: `${base}/tenants/${encodeURIComponent(input.tenantId)}/edge-provisioning`,
        schema: edgeProvisioningViewSchema.nullable(),
        signal: input.signal,
      });
    },
    async readCollegeOnboarding(input) {
      return http.request({
        path: `${base}/tenants/${encodeURIComponent(input.tenantId)}/onboarding`,
        schema: collegeOnboardingViewSchema,
        signal: input.signal,
      });
    },
    listAiServices: listGetter(http, `${base}/ai-services`, aiServiceSchema),
    listKnowledgeCollections: listGetter(
      http,
      `${base}/knowledge/collections`,
      knowledgeCollectionSchema,
    ),
    listDocuments: listGetter(http, `${base}/documents`, knowledgeDocumentSchema),
    listPolicies: listGetter(http, `${base}/governance/policies`, policyViewSchema),
    listApprovals: listGetter(http, `${base}/governance/approvals`, approvalRequestSchema),
    async submitApprovalDecision(input: ApprovalDecisionInput) {
      return http.request({
        path: `${base}/governance/approvals/${encodeURIComponent(input.approvalId)}/decision`,
        method: 'POST',
        body: {
          decision: input.decision,
          payload_digest: input.payloadDigest,
          justification: input.justification,
        },
        schema: approvalDecisionResultSchema,
        signal: input.signal,
      });
    },
    listMetrics: listGetter(http, `${base}/analytics/metrics`, metricsSchema),
    listAuditEvents: listGetter(http, `${base}/audit/events`, auditEventViewSchema),
    listMonitoredServices: listGetter(http, `${base}/monitoring/services`, serviceHealthSchema),
    listRoles: listGetter(http, `${base}/access/roles`, roleSchema),
    listAccessGrants: listGetter(http, `${base}/access/grants`, accessGrantSchema),
    listMemoryRecords: listGetter(http, `${base}/memory/records`, memoryRecordSchema),
  };
}

function createTenantClient(http: HttpClient): TenantClient {
  const base = '/tenant';
  return {
    async getSummary(signal?: AbortSignal) {
      return http.request({ path: `${base}/summary`, schema: dashboardSummarySchema, signal });
    },
    listConnectors: listGetter(http, `${base}/connectors`, connectorWithHealthSchema),
    listDataSources: listGetter(http, `${base}/data-sources`, dataSourceSchema),
    listSchemaMappings: listGetter(http, `${base}/schema-mappings`, schemaMappingSchema),
    listDocuments: listGetter(http, `${base}/documents`, knowledgeDocumentSchema),
    listKnowledgeCollections: listGetter(
      http,
      `${base}/knowledge/collections`,
      knowledgeCollectionSchema,
    ),
    listPolicies: listGetter(http, `${base}/policies`, policyViewSchema),
    listAuditEvents: listGetter(http, `${base}/audit/events`, auditEventViewSchema),
    listHealthChecks: listGetter(http, `${base}/health/checks`, healthCheckSchema),
  };
}

/**
 * Build the AI settings client (**ADR-0080**).
 *
 * @param http - The shared transport.
 * @returns The AI settings client.
 */
function createAiUsageClient(
  http: ReturnType<typeof createHttpClient>,
  basePath: string,
): AiUsageClient {
  const usageBase = '/control-plane/settings/ai/usage';

  // ADR-0080. Five reads under one capability. Money arrives as fixed-precision
  // strings and stays that way: nothing here parses one into a number.
  return {
    async summary(filters, signal) {
      return http.request({
        path: `${usageBase}/summary`,
        query: usageQuery(filters),
        schema: usageSummarySchema,
        signal,
      });
    },
    async byCollege(filters, signal) {
      return http.request({
        path: `${usageBase}/colleges`,
        query: usageQuery(filters),
        schema: collegeCostPageSchema,
        signal,
      });
    },
    async breakdown(filters, signal) {
      return http.request({
        path: `${usageBase}/breakdown`,
        query: usageQuery(filters),
        schema: modelCostPageSchema,
        signal,
      });
    },
    async trend(filters, signal) {
      return http.request({
        path: `${usageBase}/trend`,
        query: usageQuery(filters),
        schema: dailyCostSeriesSchema,
        signal,
      });
    },
    exportHref(filters) {
      // A link rather than a fetch: the browser's own download handling gives
      // the file a name and a progress indicator, and the session cookie it
      // already holds is the only credential involved.
      const query = new URLSearchParams(usageQuery(filters)).toString();
      const suffix = query === '' ? '' : `?${query}`;
      return `${basePath}${usageBase}/export${suffix}`;
    },
  };
}

function createAiSettingsClient(http: ReturnType<typeof createHttpClient>): AiSettingsClient {
  const aiBase = '/control-plane/settings/ai';

  // ADR-0080. Every method names one path and one verb; none of them can
  // receive a provider secret, because no response schema has a member for one.
  return {
    async listProviders(signal?: AbortSignal) {
      return http.request({ path: `${aiBase}/providers`, schema: providerListSchema, signal });
    },
    async createProvider(input) {
      return http.request({
        path: `${aiBase}/providers`,
        method: 'POST',
        body: {
          display_name: input.display_name,
          provider_type: input.provider_type,
          enabled: input.enabled ?? false,
          config: input.config ?? {},
        },
        schema: providerViewSchema,
        signal: input.signal,
      });
    },
    async amendProvider(input) {
      const body: Record<string, unknown> = {};
      if (input.display_name !== undefined) {
        body['display_name'] = input.display_name;
      }
      if (input.enabled !== undefined) {
        body['enabled'] = input.enabled;
      }
      return http.request({
        path: `${aiBase}/providers/${encodeURIComponent(input.provider_id)}`,
        method: 'PATCH',
        body,
        schema: providerViewSchema,
        signal: input.signal,
      });
    },
    async deleteProvider(providerId: string, signal?: AbortSignal) {
      await http.request({
        path: `${aiBase}/providers/${encodeURIComponent(providerId)}`,
        method: 'DELETE',
        // A `204` carries nothing, and demanding a body of it would turn a
        // successful removal into a validation failure.
        schema: z.unknown(),
        signal,
      });
    },
    async rotateCredential(input) {
      // The one call that carries a secret. It is sent and not retained: no
      // caller stores the input, and the response has no field to echo it into.
      return http.request({
        path: `${aiBase}/providers/${encodeURIComponent(input.provider_id)}/credential`,
        method: 'POST',
        body: { api_key: input.api_key },
        schema: providerViewSchema,
        signal: input.signal,
      });
    },
    async testProvider(providerId: string, signal?: AbortSignal) {
      return http.request({
        path: `${aiBase}/providers/${encodeURIComponent(providerId)}/test`,
        method: 'POST',
        schema: providerViewSchema,
        signal,
      });
    },
    async listModels(signal?: AbortSignal) {
      return http.request({ path: `${aiBase}/models`, schema: modelListSchema, signal });
    },
    async createModel(input) {
      return http.request({
        path: `${aiBase}/models`,
        method: 'POST',
        body: {
          provider_id: input.provider_id,
          display_name: input.display_name,
          provider_model_ref: input.provider_model_ref,
          enabled: input.enabled ?? false,
        },
        schema: modelViewSchema,
        signal: input.signal,
      });
    },
    async amendModel(input) {
      const body: Record<string, unknown> = {};
      if (input.display_name !== undefined) {
        body['display_name'] = input.display_name;
      }
      if (input.enabled !== undefined) {
        body['enabled'] = input.enabled;
      }
      return http.request({
        path: `${aiBase}/models/${encodeURIComponent(input.model_id)}`,
        method: 'PATCH',
        body,
        schema: modelViewSchema,
        signal: input.signal,
      });
    },
    async deleteModel(modelId: string, signal?: AbortSignal) {
      await http.request({
        path: `${aiBase}/models/${encodeURIComponent(modelId)}`,
        method: 'DELETE',
        schema: z.unknown(),
        signal,
      });
    },
    async readRouting(signal?: AbortSignal) {
      return http.request({ path: `${aiBase}/routing`, schema: routingViewSchema, signal });
    },
    async activateRouting(input) {
      const body: Record<string, unknown> = {
        entries: input.entries.map((entry) => ({
          position: entry.position,
          model_id: entry.model_id,
        })),
      };
      if (input.expected_version !== undefined) {
        body['expected_version'] = input.expected_version;
      }
      return http.request({
        path: `${aiBase}/routing`,
        method: 'PUT',
        body,
        schema: routingViewSchema,
        signal: input.signal,
      });
    },
  };
}

/**
 * Build the production client set.
 *
 * @param config - Transport configuration; `basePath` must be same-origin.
 * @returns Session, control-plane, and tenant clients backed by the BFF.
 */
export function createApiClients(config: HttpClientConfig): ApiClients {
  const http = createHttpClient(config);
  return {
    session: createSessionClient(http),
    controlPlane: createControlPlaneClient(http),
    tenant: createTenantClient(http),
    aiSettings: createAiSettingsClient(http),
    aiUsage: createAiUsageClient(http, config.basePath),
  };
}
